import { Component, EventEmitter, Input, Output } from '@angular/core'

import { FormsModule } from '@angular/forms'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { TaskFormComponent } from '@app/routes/tasks/components/task-form/task-form.component'
import { TaskItemComponent } from '@app/routes/tasks/components/task-item/task-item.component'
import { UserTask, TaskCheckboxChangeEvent } from '@app/core/_models/task.model'
export interface QueuedTask {
    title: string
    description?: string
}

const MAX_QUEUED_TASKS = 20

@Component({
    selector: 'fm-booking-hub-task-form',
    standalone: true,
    imports: [FormsModule, ButtonModule, TooltipModule, TaskFormComponent, TaskItemComponent],
    templateUrl: './booking-hub-task-form.component.html',
    styleUrl: './booking-hub-task-form.component.scss',
})
export class BookingHubTaskFormComponent {
    @Input() tasks: QueuedTask[] = []
    @Output() tasksChange = new EventEmitter<QueuedTask[]>()

    showCreateForm = false
    editingIndex: number | null = null

    newTaskTitle = ''
    newTaskDescription = ''
    editingTaskTitle = ''
    editingTaskDescription = ''

    onToggleCreateForm(): void {
        if (this.showCreateForm) {
            this.cancelCreate()
        } else {
            this.showCreateForm = true
            this.editingIndex = null
            this.newTaskTitle = ''
            this.newTaskDescription = ''
        }
    }

    onCreateTask(): void {
        const title = this.newTaskTitle.replace(/\n/g, ' ').trim()
        if (!title || title.length === 0) return

        if (title.length > 500) {
            // Could show error, but keeping simple for now
            return
        }

        if (this.tasks.length >= MAX_QUEUED_TASKS) {
            // Maximum number of tasks reached
            return
        }

        const newTask: QueuedTask = {
            title,
            description: this.newTaskDescription.trim() || undefined,
        }

        this.tasks = [...this.tasks, newTask]
        this.tasksChange.emit(this.tasks)
        this.cancelCreate()
    }

    get canAddMoreTasks(): boolean {
        return this.tasks.length < MAX_QUEUED_TASKS
    }

    cancelCreate(): void {
        this.showCreateForm = false
        this.newTaskTitle = ''
        this.newTaskDescription = ''
    }

    onStartEdit(index: number): void {
        this.editingIndex = index
        this.editingTaskTitle = this.tasks[index].title
        this.editingTaskDescription = this.tasks[index].description || ''
        this.showCreateForm = false
    }

    onSaveEdit(): void {
        if (this.editingIndex === null) return

        const title = this.editingTaskTitle.replace(/\n/g, ' ').trim()
        if (!title || title.length === 0) return

        if (title.length > 500) {
            return
        }

        const updatedTasks = [...this.tasks]
        updatedTasks[this.editingIndex] = {
            title,
            description: this.editingTaskDescription.trim() || undefined,
        }

        this.tasks = updatedTasks
        this.tasksChange.emit(this.tasks)
        this.cancelEdit()
    }

    cancelEdit(): void {
        this.editingIndex = null
        this.editingTaskTitle = ''
        this.editingTaskDescription = ''
    }

    onDeleteTask(index: number): void {
        const updatedTasks = this.tasks.filter((_, i) => i !== index)
        this.tasks = updatedTasks
        this.tasksChange.emit(this.tasks)
    }

    /**
     * Convert a QueuedTask to a UserTask-like object for display in task-item component.
     * These are temporary tasks that haven't been created yet, so we use placeholder values.
     */
    queuedTaskToUserTask(queuedTask: QueuedTask, index: number): UserTask {
        return {
            id: `queued-${index}`, // Temporary ID for display
            title: queuedTask.title,
            description: queuedTask.description || null,
            createdAt: new Date().toISOString(),
            completedAt: null,
            attachments: [],
        }
    }

    onTaskClick(task: UserTask): void {
        // Extract index from temporary ID
        const index = parseInt(task.id.replace('queued-', ''), 10)
        if (!isNaN(index)) {
            this.onStartEdit(index)
        }
    }

    onCheckboxChange(_event: TaskCheckboxChangeEvent): void {
        // Queued tasks can't be completed, so ignore checkbox changes
    }

    onDeleteTaskFromItem(task: UserTask): void {
        // Extract index from temporary ID
        const index = parseInt(task.id.replace('queued-', ''), 10)
        if (!isNaN(index)) {
            this.onDeleteTask(index)
        }
    }

    onOverflowDeleteWhileEditing(): void {
        if (this.editingIndex === null) return
        const index = this.editingIndex
        this.cancelEdit()
        this.onDeleteTask(index)
    }
}
